import React, { useState, useEffect } from 'react';
import API_URL from '../config';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SharedNav from './SharedNav';
import './UserManagement.css';
import ReassignModal from './ReassignModal';
import canReassign from './shared/canReassign';

// Defined at module scope, not nested inside UserManagement -- nesting it in the parent's
// render body gave it a new function identity on every parent render, so React treated it as
// a brand-new component type each time and remounted the <input> from scratch, which is why
// the search box lost focus after every single keystroke. allStations is passed as a prop
// instead of closed over, since it's no longer in the same scope.
function StationPicker({ selectedIds, onToggle, search, onSearch, allStations }) {
  const filtered = allStations.filter(s => {
    const q = search.toLowerCase();
    return !q || String(s.station_number).includes(q) ||
      (s.city || '').toLowerCase().includes(q) ||
      (s.state || '').toLowerCase().includes(q);
  });
  const selected = allStations.filter(s => selectedIds.includes(s.id));
  return (
    <div className="cs-station-picker">
      {selected.length > 0 && (
        <div className="cs-station-chips">
          {selected.map(s => (
            <span key={s.id} className="cs-station-chip">
              #{s.station_number}{s.city ? ` — ${s.city}` : ''}
              <button type="button" onClick={() => onToggle(s.id)}>×</button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text"
        className="cs-station-search-input"
        placeholder="Search by station # or city..."
        value={search}
        onChange={e => onSearch(e.target.value)}
      />
      {search && (
        <div className="cs-station-dropdown">
          {filtered.slice(0, 20).map(s => (
            <div
              key={s.id}
              className={`cs-station-option${selectedIds.includes(s.id) ? ' selected' : ''}`}
              onClick={() => { onToggle(s.id); onSearch(''); }}
            >
              <span className="cs-station-num">#{s.station_number}</span>
              {s.city && <span className="cs-station-city"> — {s.city}, {s.state}</span>}
              {selectedIds.includes(s.id) && <span className="cs-station-check">✓</span>}
            </div>
          ))}
          {filtered.length === 0 && <div className="cs-station-option-empty">No stations found</div>}
        </div>
      )}
    </div>
  );
}

// Mirrors the server-side recruitingAdminCanManage() check in routes/users.js -- recruiting_admin
// can only ever act on recruiter/customer rows, never admin or (including its own) recruiting_admin
// rows. This is UX only; the server enforces the real restriction on every request either way.
function recruitingAdminCanManage(role) {
  const r = (role || '').toLowerCase();
  return r === 'recruiter' || r === 'customer';
}

function UserManagement({ user, onLogout }) {
  const navigate = useNavigate();
  const isRecruitingAdmin = user?.role?.toLowerCase() === 'recruiting_admin';
  const [users, setUsers] = useState([]);
  const [reassignFrom, setReassignFrom] = useState(null); // user row whose candidates are being moved
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showCompanyManagement, setShowCompanyManagement] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    communication_preference: 'Email',
    password: '',
    role: '',
    company_id: '',
    qbo_payment_link: '',
    rc_jwt: ''
  });
  const [newCompanyName, setNewCompanyName] = useState('');
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editCompanyAuthorizedOwnerId, setEditCompanyAuthorizedOwnerId] = useState('');
  const [error, setError] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  // Station management
  const [allStations, setAllStations] = useState([]);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [editCompanyStationIds, setEditCompanyStationIds] = useState([]);
  const [editCompanyStationSearch, setEditCompanyStationSearch] = useState('');
  const [formStationIds, setFormStationIds] = useState([]);
  const [formStationSearch, setFormStationSearch] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
    fetchCompanies();
    fetchAllStations();
  }, []);

  const fetchAllStations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/map-stations`, { headers: { Authorization: `Bearer ${token}` } });
      setAllStations(res.data.filter(s => s.station_number).sort((a, b) => a.station_number - b.station_number));
    } catch (err) { console.error('Error fetching stations:', err); }
  };

  const fetchCompanyStations = async (companyId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/companies/${companyId}/stations`, { headers: { Authorization: `Bearer ${token}` } });
      return res.data.map(s => s.id);
    } catch (err) { return []; }
  };

  const saveCompanyStations = async (companyId, stationIds) => {
    const token = localStorage.getItem('token');
    await axios.put(`${API_URL}/api/companies/${companyId}/stations`, { station_ids: stationIds }, { headers: { Authorization: `Bearer ${token}` } });
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) {
      alert('Please enter a company name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/companies`,
        { name: newCompanyName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanies([...companies, response.data]);
      setFormData({ ...formData, company_id: response.data.id });
      setNewCompanyName('');
      setShowCompanyModal(false);
      alert('Company created successfully!');
    } catch (err) {
      alert(err.response?.data?.error || 'Error creating company');
    }
  };

  const handleEditCompany = async (company) => {
    setEditingCompany(company);
    setEditCompanyName(company.name);
    setEditCompanyAuthorizedOwnerId(company.authorized_owner_id || '');
    setEditCompanyStationSearch('');
    const ids = await fetchCompanyStations(company.id);
    setEditCompanyStationIds(ids);
    setShowEditCompanyModal(true);
  };

  const handleUpdateCompany = async () => {
    if (!editCompanyName.trim()) {
      alert('Please enter a company name');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        `${API_URL}/api/companies/${editingCompany.id}`,
        { name: editCompanyName, authorized_owner_id: editCompanyAuthorizedOwnerId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await saveCompanyStations(editingCompany.id, editCompanyStationIds);
      setCompanies(companies.map(c =>
        c.id === editingCompany.id
          ? { ...c, name: editCompanyName, authorized_owner_id: data.authorized_owner_id, authorized_owner_name: data.authorized_owner_name }
          : c
      ));
      setShowEditCompanyModal(false);
      setEditingCompany(null);
      setEditCompanyName('');
      setEditCompanyAuthorizedOwnerId('');
      setEditCompanyStationIds([]);
    } catch (err) {
      alert(err.response?.data?.error || 'Error updating company');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/companies/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCompanies(companies.filter(c => c.id !== companyId));
      alert('Company deleted successfully!');
    } catch (err) {
      alert(err.response?.data?.error || 'Error deleting company');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (editingUser) {
        // Update existing user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if left blank
        }
        if (!updateData.rc_jwt) {
          delete updateData.rc_jwt; // Don't touch the existing RingCentral connection if left blank
        }
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new user
        await axios.post(`${API_URL}/api/users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      // Save station assignments to the company if one was selected
      if (formData.company_id && formStationIds.length > 0) {
        await saveCompanyStations(formData.company_id, formStationIds);
      }
      setShowForm(false);
      setEditingUser(null);
      setFormData({ name: '', email: '', phone: '', communication_preference: 'Email', password: '', role: '', company_id: '', qbo_payment_link: '', rc_jwt: '' });
      setFormStationIds([]);
      setFormStationSearch('');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || (editingUser ? 'Error updating user' : 'Error adding user'));
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      communication_preference: user.communication_preference || 'Email',
      password: '',
      role: user.role || '',
      company_id: user.company_id || '',
      qbo_payment_link: user.qbo_payment_link || '',
      rc_jwt: ''
    });
    setShowForm(true);
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err) {
      alert('Error deleting user');
    }
  };

  const handleToggleLock = async (u) => {
    const isLocked = !!u.locked_at;
    const action = isLocked ? 'unlock' : 'lock';
    if (!window.confirm(`${isLocked ? 'Unlock' : 'Lock'} account for ${u.name || u.email}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/users/${u.id}/toggle-lock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err) {
      alert(`Error trying to ${action} account`);
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/users/${id}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (err) {
      alert('Error updating user role');
    }
  };

  const openAssignModal = (u) => {
    setSelectedUser(u);
    // Parse assigned_companies instead of assigned_clients
    const assigned = u.assigned_companies ? u.assigned_companies.split(',').map(id => parseInt(id)) : [];
    setSelectedClients(assigned);
    setShowAssignModal(true);
  };

  const handleAssignClients = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/users/${selectedUser.id}/assign-companies`,
        { companyIds: selectedClients },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowAssignModal(false);
      fetchUsers();
    } catch (err) {
      alert('Error assigning companies');
    }
  };

  const toggleClient = (clientId) => {
    setSelectedClients(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handlePopulateFakeDrivers = async (userId, userName) => {
    if (!window.confirm(`Create 5 fake driver candidates for ${userName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/populate-fake-drivers/${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message || 'Fake drivers created successfully!');
    } catch (err) {
      alert(err.response?.data?.error || 'Error creating fake drivers');
    }
  };

  return (
    <div className="user-management">
      <SharedNav user={user} onLogout={onLogout} />

      <div className="dashboard-content">
        <div className="controls">
          <div className="um-summary">
            <div className="um-sum-card um-sum-users">
              <span className="um-sum-count">{users.length}</span>
              <span className="um-sum-label">Users</span>
            </div>
            <div className="um-sum-card um-sum-customers">
              <span className="um-sum-count">{users.filter(u => u.role?.toLowerCase() === 'customer').length}</span>
              <span className="um-sum-label">Customers</span>
            </div>
            <div className="um-sum-card um-sum-recruiters">
              <span className="um-sum-count">{users.filter(u => ['recruiter', 'recruiting_admin'].includes(u.role?.toLowerCase())).length}</span>
              <span className="um-sum-label">Recruiters</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => {
              if (showForm) {
                setShowForm(false);
                setEditingUser(null);
                setFormData({ name: '', email: '', password: '', role: isRecruitingAdmin ? 'recruiter' : '', company_id: '' });
              } else {
                setEditingUser(null);
                setFormData(prev => ({ ...prev, role: isRecruitingAdmin ? 'recruiter' : prev.role }));
                setShowForm(true);
              }
            }} className="add-btn">
              {showForm ? 'Cancel' : '+ Add User'}
            </button>
            {!isRecruitingAdmin && (
              <button
                onClick={() => setShowCompanyManagement(true)}
                className="nav-btn"
              >
                Manage Companies
              </button>
            )}
          </div>
        </div>

        {showForm && (
          <div className="add-user-form">
            <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
            <form onSubmit={handleAddUser}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Full name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number (with country code, e.g., +1234567890)</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1234567890"
                  pattern="^\+[1-9]\d{1,14}$"
                  title="Please enter phone number with country code (e.g., +1234567890)"
                />
              </div>
              <div className="form-group">
                <label>Communication Preference</label>
                <select
                  value={formData.communication_preference}
                  onChange={(e) => setFormData({ ...formData, communication_preference: e.target.value })}
                  required
                >
                  <option value="Email">Email</option>
                  <option value="Text">Text</option>
                  <option value="Both">Both</option>
                </select>
              </div>
              {!isRecruitingAdmin && (
                <div className="form-group">
                  <label>Company (for Customer users)</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <select
                      value={formData.company_id}
                      onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                      style={{ flex: 1 }}
                    >
                      <option value="">-- No Company --</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowCompanyModal(true)}
                      className="btn-secondary"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      + New Company
                    </button>
                  </div>
                </div>
              )}
              {formData.company_id && !isRecruitingAdmin && (
                <div className="form-group">
                  <label>Stations (for this company)</label>
                  <StationPicker
                    selectedIds={formStationIds}
                    onToggle={id => setFormStationIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                    search={formStationSearch}
                    onSearch={setFormStationSearch}
                    allStations={allStations}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Password {editingUser && '(leave blank to keep current)'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  minLength="6"
                  placeholder={editingUser ? 'Leave blank to keep current password' : ''}
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                {isRecruitingAdmin ? (
                  editingUser?.role?.toLowerCase() === 'customer' ? (
                    <select value="customer" disabled>
                      <option value="customer">Customer (role locked — admin only)</option>
                    </select>
                  ) : (
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      required
                    >
                      <option value="recruiter">Recruiter</option>
                      <option value="recruiting_admin">Recruiting Admin</option>
                    </select>
                  )
                ) : (
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                  >
                    <option value="">-- Select Role --</option>
                    <option value="recruiter">Recruiter</option>
                    <option value="recruiting_admin">Recruiting Admin</option>
                    <option value="customer">Customer</option>
                    <option value="admin">Admin</option>
                  </select>
                )}
              </div>
              {!isRecruitingAdmin && (formData.role === 'customer' || (!editingUser && formData.role === 'customer')) && (
                <div className="form-group">
                  <label>QBO Payment Link</label>
                  <input
                    type="url"
                    value={formData.qbo_payment_link}
                    onChange={(e) => setFormData({ ...formData, qbo_payment_link: e.target.value })}
                    placeholder="https://app.qbo.intuit.com/app/invoice?..."
                  />
                </div>
              )}
              {editingUser && ['recruiter', 'recruiting_admin', 'admin'].includes(formData.role) && (
                <div className="form-group">
                  <label>
                    RingCentral Connection {editingUser.has_rc_jwt ? '(connected — paste to replace)' : '(not connected)'}
                  </label>
                  <textarea
                    value={formData.rc_jwt}
                    onChange={(e) => setFormData({ ...formData, rc_jwt: e.target.value })}
                    placeholder={editingUser.has_rc_jwt ? 'Leave blank to keep current connection' : 'Paste JWT credential from developers.ringcentral.com'}
                    rows={3}
                    style={{ fontFamily: 'monospace', fontSize: '11px' }}
                  />
                  <small style={{ color: '#888' }}>
                    Needed for intro texts to send from this recruiter's own RingCentral number. They generate this themselves at developers.ringcentral.com (TextMyCustomer app → Credentials → JWT).
                  </small>
                </div>
              )}
              {error && <div className="error-message">{error}</div>}
              <button type="submit" className="btn-primary">
                {editingUser ? 'Update User' : 'Add User'}
              </button>
            </form>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <div className="table-scroll-container mtn-scroll-x">
          <table className="user-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Comm. Pref.</th>
                <th>Company</th>
                <th>Role</th>
                {!isRecruitingAdmin && (
                  <th>Password
                    <button
                      onClick={() => setShowPasswords(p => !p)}
                      title={showPasswords ? 'Hide passwords' : 'Show passwords'}
                      style={{
                        marginLeft: 6, background: 'none', border: 'none',
                        cursor: 'pointer', color: showPasswords ? '#90caf9' : '#aaa',
                        fontSize: 14, padding: '0 2px', verticalAlign: 'middle'
                      }}
                    >
                      {showPasswords ? '🙈' : '👁'}
                    </button>
                  </th>
                )}
                <th>Assigned Clients</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const manageable = !isRecruitingAdmin || recruitingAdminCanManage(u.role);
                return (
                <tr key={u.id}>
                  <td>{u.id}</td>
                  <td>{u.name || '-'}</td>
                  <td>{u.email}</td>
                  <td>{u.phone || '-'}</td>
                  <td>{u.communication_preference || 'Email'}</td>
                  <td>{u.company_name || '-'}</td>
                  <td>
                    {isRecruitingAdmin ? (
                      <span style={{ textTransform: 'capitalize' }}>{(u.role || '').replace('_', ' ')}</span>
                    ) : (
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        className="role-select"
                      >
                        <option value="recruiter">Recruiter</option>
                        <option value="recruiting_admin">Recruiting Admin</option>
                        <option value="customer">Customer</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}
                    {['recruiter', 'recruiting_admin', 'admin'].includes(u.role?.toLowerCase()) && (
                      <span
                        title={u.has_rc_jwt ? 'RingCentral connected' : 'RingCentral not connected — intro texts will be skipped for their candidates'}
                        style={{
                          display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginLeft: 6,
                          background: u.has_rc_jwt ? '#4caf50' : '#888'
                        }}
                      />
                    )}
                  </td>
                  {!isRecruitingAdmin && (
                    <td style={{ fontFamily: 'monospace', letterSpacing: showPasswords ? 0 : 2 }}>
                      {showPasswords
                        ? (u.plain_password || <span style={{ color: '#aaa', fontStyle: 'italic', fontFamily: 'inherit', letterSpacing: 0 }}>not stored</span>)
                        : '••••••••'}
                    </td>
                  )}
                  <td>
                    {(isRecruitingAdmin ? u.role?.toLowerCase() === 'recruiter' : ['recruiter', 'recruiting_admin', 'customer'].includes(u.role?.toLowerCase())) ? (
                      <button
                        onClick={() => openAssignModal(u)}
                        className="btn-assign"
                      >
                        Assign Companies ({u.assigned_companies ? u.assigned_companies.split(',').length : 0})
                      </button>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{new Date(u.created_at).toLocaleDateString()}</td>
                  <td>
                    {!manageable ? (
                      <span style={{ color: '#999', fontStyle: 'italic' }}>Admin-managed</span>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEditUser(u)}
                          className="mtn-btn mtn-btn-edit"
                          style={{ marginRight: '6px' }}
                        >
                          <span className="mtn-ic">✏</span>Edit
                        </button>
                        {!isRecruitingAdmin && (
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            className={`mtn-btn mtn-btn-delete${u.id === user.id ? ' mtn-btn-disabled' : ''}`}
                            disabled={u.id === user.id}
                            style={{ marginRight: '6px' }}
                          >
                            Delete
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleLock(u)}
                          className={`mtn-btn ${u.locked_at ? 'mtn-btn-send' : 'mtn-btn-delete'}${u.id === user.id ? ' mtn-btn-disabled' : ''}`}
                          disabled={u.id === user.id}
                          style={{ marginRight: '6px' }}
                          title={u.locked_at ? 'Account is locked — click to unlock' : 'Click to lock account'}
                        >
                          {u.locked_at ? 'Unlock' : 'Lock'}
                        </button>
                        {!isRecruitingAdmin && u.role?.toLowerCase() === 'customer' && (
                          <button
                            onClick={() => handlePopulateFakeDrivers(u.id, u.name || u.email)}
                            className="mtn-btn mtn-btn-neutral"
                            title="Create 5 fake driver candidates"
                          >
                            + Fake Drivers
                          </button>
                        )}
                      </>
                    )}
                    {canReassign(user) && ['recruiter', 'recruiting_admin', 'admin'].includes(u.role?.toLowerCase()) && (
                      <button
                        onClick={() => setReassignFrom(u)}
                        className="mtn-btn mtn-btn-neutral mtn-btn-reassign"
                        style={{ marginLeft: '6px' }}
                        title="Move this recruiter's candidates to other recruiters"
                      >
                        Reassign
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

      </div>

      {reassignFrom && <ReassignModal fromUser={reassignFrom} onClose={() => setReassignFrom(null)} />}

      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content assign-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Assign Companies to {selectedUser.email}</h3>
            <div className="clients-list">
              {companies.length === 0 ? (
                <p>No companies available</p>
              ) : (
                companies.map(company => (
                  <label key={company.id} className="client-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(company.id)}
                      onChange={() => toggleClient(company.id)}
                    />
                    <span>{company.name}</span>
                  </label>
                ))
              )}
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowAssignModal(false)} className="btn-cancel">
                Cancel
              </button>
              <button onClick={handleAssignClients} className="btn-submit">
                Save Assignment
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompanyModal && (
        <div className="modal-overlay" onClick={() => setShowCompanyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <h3>Create New Company</h3>
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                placeholder="Enter company name"
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => { setShowCompanyModal(false); setNewCompanyName(''); }} className="btn-cancel">
                Cancel
              </button>
              <button onClick={handleCreateCompany} className="btn-submit">
                Create Company
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompanyManagement && (
        <div className="modal-overlay" onClick={() => setShowCompanyManagement(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', minHeight: '500px' }}>
            <h3>Company Management</h3>
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Manage all companies in the system. Companies are used to organize users and job postings.
            </p>

            {companies.length === 0 ? (
              <p>No companies found. Create one to get started.</p>
            ) : (
              <div className="table-scroll-container mtn-scroll-x">
              <table className="user-table" style={{ marginTop: '20px' }}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Company Name</th>
                    <th>Authorized Owner</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map(company => (
                    <tr key={company.id}>
                      <td>{company.id}</td>
                      <td>{company.name}</td>
                      <td>{company.authorized_owner_name || '—'}</td>
                      <td>{company.account_status || '—'}</td>
                      <td>{new Date(company.created_at).toLocaleDateString()}</td>
                      <td>
                        <button
                          onClick={() => handleEditCompany(company)}
                          className="btn-secondary"
                          style={{ marginRight: '8px', padding: '6px 12px' }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteCompany(company.id)}
                          className="btn-delete"
                          style={{ padding: '6px 12px' }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button
                onClick={() => {
                  setEditingCompany(null);
                  setEditCompanyName('');
                  setShowCompanyManagement(false);
                }}
                className="btn-cancel"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditCompanyModal && editingCompany && (
        <div className="modal-overlay" onClick={() => setShowEditCompanyModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <h3>Edit Company</h3>
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                value={editCompanyName}
                onChange={e => setEditCompanyName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Authorized Owner</label>
              <select
                value={editCompanyAuthorizedOwnerId}
                onChange={e => setEditCompanyAuthorizedOwnerId(e.target.value)}
              >
                <option value="">— None —</option>
                {[...customers].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.company_names ? ` — ${c.company_names}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Stations</label>
              <StationPicker
                selectedIds={editCompanyStationIds}
                onToggle={id => setEditCompanyStationIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                search={editCompanyStationSearch}
                onSearch={setEditCompanyStationSearch}
                allStations={allStations}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowEditCompanyModal(false)} className="btn-cancel">Cancel</button>
              <button onClick={handleUpdateCompany} className="btn-submit">Save</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default UserManagement;
